/**
 * megaMenuModel.js
 * ────────────────
 * The mega menu content model shared by the menu manager (builder), the
 * Navigation block (renderer) and tests. Mirrors MenuEditService::NormaliseMega
 * on the server.
 *
 *   megaContent = {
 *     layout: '2col' | '3col' | '4col' | 'featured',
 *     columns: [ { width: 1..3, blocks: Block[] } ]
 *   }
 *
 *   Block kinds:
 *     { type: 'links',   heading, itemIds: [] }       itemIds empty = "the links no other group picked"
 *     { type: 'image',   src, mediaID, url, caption }
 *     { type: 'text',    heading, html }
 *     { type: 'button',  label, url, style: 'primary' | 'outline' }
 *     { type: 'stories', heading, categoryId, count 1–6, layout: 'cards' | 'list' }   posts[] filled server-side
 *     { type: 'divider' }
 *
 * The older one-slot-per-column shape ({ slots: [...] }) is upgraded on read.
 */

export var MEGA_LAYOUTS = [
	{ value: '2col', label: '2 columns', widths: [1, 1] },
	{ value: '3col', label: '3 columns', widths: [1, 1, 1] },
	{ value: '4col', label: '4 columns', widths: [1, 1, 1, 1] },
	{ value: 'featured', label: 'Links + feature', widths: [2, 1] }
];

export var BLOCK_KINDS = [
	{ type: 'links', label: 'Links', icon: 'fa-list-ul', hint: 'A group of this menu\'s child links' },
	{ type: 'stories', label: 'Stories', icon: 'fa-newspaper-o', hint: 'Latest posts, optionally from one category' },
	{ type: 'image', label: 'Image', icon: 'fa-picture-o', hint: 'A picture, optionally linked' },
	{ type: 'text', label: 'Text', icon: 'fa-font', hint: 'A heading and a short paragraph' },
	{ type: 'button', label: 'Button', icon: 'fa-hand-pointer-o', hint: 'A call to action' },
	{ type: 'divider', label: 'Divider', icon: 'fa-minus', hint: 'A thin line between blocks' }
];

export function layoutWidths(layout) {
	var l = MEGA_LAYOUTS.find(function (x) { return x.value === layout; });
	return (l || MEGA_LAYOUTS[1]).widths.slice();
}

/** A fresh block of a kind with its default fields. */
export function newBlock(type) {
	switch (type) {
		case 'stories': return { type: 'stories', heading: '', categoryId: null, count: 3, layout: 'cards' };
		case 'image': return { type: 'image', src: '', mediaID: null, url: '', caption: '' };
		case 'text': return { type: 'text', heading: '', html: '' };
		case 'button': return { type: 'button', label: '', url: '', style: 'primary' };
		case 'divider': return { type: 'divider' };
		default: return { type: 'links', heading: '', itemIds: [] };
	}
}

function clean(block) {
	if (!block || typeof block !== 'object') return null;
	var kind = BLOCK_KINDS.find(function (k) { return k.type === block.type; });
	if (!kind) return null;
	var out = newBlock(block.type);
	Object.keys(out).forEach(function (k) {
		if (block[k] !== undefined && block[k] !== null) out[k] = block[k];
	});
	if (out.type === 'stories') {
		out.count = Math.max(1, Math.min(parseInt(out.count, 10) || 3, 6));
		out.layout = out.layout === 'list' ? 'list' : 'cards';
		out.categoryId = out.categoryId ? parseInt(out.categoryId, 10) : null;
		if (Array.isArray(block.posts)) out.posts = block.posts;
	}
	if (out.type === 'links') out.itemIds = Array.isArray(out.itemIds) ? out.itemIds.map(String) : [];
	if (out.type === 'button') out.style = out.style === 'outline' ? 'outline' : 'primary';
	return out;
}

/** Upgrades the earlier one-slot-per-column shape to columns of blocks. */
function fromSlots(slots) {
	return slots.map(function (slot) {
		var s = slot || {};
		if (s.type === 'posts') return { blocks: [{ type: 'stories', heading: s.heading || '', categoryId: s.categoryId || null, count: s.count || 3, layout: s.layout || 'cards', posts: s.posts }] };
		if (s.type === 'promo') {
			var blocks = [];
			if (s.image) blocks.push({ type: 'image', src: s.image });
			if (s.heading || s.text) blocks.push({ type: 'text', heading: s.heading || '', html: s.text || '' });
			if (s.buttonLabel) blocks.push({ type: 'button', label: s.buttonLabel, url: s.buttonUrl || '' });
			return { blocks: blocks };
		}
		return { blocks: [{ type: 'links', heading: s.heading || '', itemIds: [] }] };
	});
}

/**
 * Normalises any stored megaContent (new, old, partial or null) into the
 * canonical shape for `layout`: one column per layout slot, widths filled in,
 * unknown blocks dropped.
 *
 * @param {string} layout
 * @param {Object|null} content
 * @returns {{ layout: string, columns: Array<{ width: number, blocks: Array }> }}
 */
export function normaliseMega(layout, content) {
	var widths = layoutWidths(layout);
	var cols = [];
	if (content && Array.isArray(content.columns)) cols = content.columns;
	else if (content && Array.isArray(content.slots)) cols = fromSlots(content.slots);

	var out = [];
	for (var i = 0; i < widths.length; i++) {
		var c = cols[i] || {};
		var blocks = Array.isArray(c.blocks) ? c.blocks.map(clean).filter(Boolean) : [];
		if (!cols[i]) blocks = [newBlock('links')];
		var w = parseInt(c.width, 10);
		out.push({ width: (w >= 1 && w <= 3) ? w : widths[i], blocks: blocks });
	}
	return { layout: layout, columns: out };
}

/** CSS grid template for the columns. */
export function gridTemplate(mega) {
	return (mega.columns || []).map(function (c) { return (c.width || 1) + 'fr'; }).join(' ');
}

/**
 * Resolves which child links each Links block shows. Blocks with explicit
 * itemIds get exactly those; blocks with none share the remaining children
 * evenly, in order.
 *
 * @param {Object} mega normalised content
 * @param {Array} children the menu item's child links
 * @returns {Object} key "col:block" → child[]
 */
export function assignLinks(mega, children) {
	children = children || [];
	var picked = {};
	var open = [];
	var result = {};
	(mega.columns || []).forEach(function (col, ci) {
		(col.blocks || []).forEach(function (b, bi) {
			if (b.type !== 'links') return;
			var key = ci + ':' + bi;
			if (b.itemIds && b.itemIds.length) {
				result[key] = b.itemIds.map(function (id) {
					picked[String(id)] = true;
					return children.find(function (ch) { return String(ch.id) === String(id); });
				}).filter(Boolean);
			} else {
				open.push(key);
			}
		});
	});
	var rest = children.filter(function (ch) { return !picked[String(ch.id)]; });
	if (open.length) {
		var per = Math.ceil(rest.length / open.length);
		open.forEach(function (key, i) { result[key] = rest.slice(i * per, i * per + per); });
	}
	return result;
}
